// src/hooks/useImageCycling.js
import { useState, useEffect, useRef } from 'react';

const useImageCycling = (images, cycleInterval = 950) => {
  // Image navigation state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef(null);
  const [resetTrigger, setResetTrigger] = useState(0);

  // Automatic image cycling
  useEffect(() => {
    if (isHovered && images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, cycleInterval);
      return () => clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isHovered, images.length, resetTrigger, cycleInterval]);

  // Image navigation handlers
  const nextImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      setResetTrigger(prev => prev + 1);
    }
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      setResetTrigger(prev => prev + 1);
    }
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Handle mouse enter
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setIsHovered(false);
    setCurrentImageIndex(0); // Reset to first image
    clearInterval(intervalRef.current);
  };

  // Manual navigation to specific image
  const goToImage = (index) => {
    if (index >= 0 && index < images.length) {
      setCurrentImageIndex(index);
      setResetTrigger(prev => prev + 1);
    }
  };

  return {
    currentImageIndex,
    isHovered,
    nextImage,
    prevImage,
    handleMouseEnter,
    handleMouseLeave,
    goToImage
  };
};

export default useImageCycling;